import {
  Box,
  Chip,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { DebouncedTextField } from './DebouncedTextField';
import { CcfTypography } from '@nice-ccf/ui-controls';
import { TranscriptMessage } from '@nice-ccf/ui-components';
import { ChangeEvent, ReactNode, useRef, useState } from 'react';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PhonePausedOutlined from '@mui/icons-material/PhonePausedOutlined';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface Props {
  messages: TranscriptMessage[];
  onChange: (messages: TranscriptMessage[]) => void;
  disabled?: boolean;
}

const senderColor: Record<string, string> = {
  agent: '#e3f0fb',
  customer: '#e8f5e9',
  system: '#f5f5f5',
};

const senderBorderColor: Record<string, string> = {
  agent: '#90caf9',
  customer: '#a5d6a7',
  system: '#bdbdbd',
};

const eventIconStyle = { fontSize: 16, color: '#526b7a' };

// Icon choices for system (call event) lines; the icons and colours match the agent app transcript
const EVENT_TYPE_OPTIONS: Array<{
  value: '' | NonNullable<TranscriptMessage['eventType']>;
  label: string;
  icon: ReactNode;
}> = [
  { value: '', label: 'Phone (default)', icon: <PhoneOutlined sx={eventIconStyle} /> },
  { value: 'hold', label: 'Call on hold', icon: <PhonePausedOutlined sx={eventIconStyle} /> },
  { value: 'resume', label: 'Call resumed', icon: <PhoneOutlined sx={eventIconStyle} /> },
  {
    value: 'error',
    label: 'Error',
    icon: <ErrorOutline sx={{ ...eventIconStyle, color: '#d32f2f' }} />,
  },
  {
    value: 'success',
    label: 'Success',
    icon: <CheckCircleOutline sx={{ ...eventIconStyle, color: '#2e7d32' }} />,
  },
];

type EventIconValue = '' | NonNullable<TranscriptMessage['eventType']>;

// Dropdown value of the "Upload custom image" action; it opens the file picker and is never stored
const UPLOAD_OPTION = '__upload__';
// Uploaded images are shrunk to this size before being stored on the line
const CUSTOM_ICON_SIZE = 48;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/svg+xml,image/gif,image/webp';

/**
 * Shrinks an uploaded image to a small square PNG data URL, keeping its aspect ratio,
 * so it can be stored on the transcript line and saved with the journey JSON.
 * @param file - image chosen by the user
 * @example toIconDataUrl(file).then(setIcon)
 */
const toIconDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The image could not be read.'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('The file is not a valid image.'));
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = CUSTOM_ICON_SIZE;
        canvas.height = CUSTOM_ICON_SIZE;
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('The image could not be processed.'));
          return;
        }
        const width = image.width || CUSTOM_ICON_SIZE;
        const height = image.height || CUSTOM_ICON_SIZE;
        const scale = Math.min(CUSTOM_ICON_SIZE / width, CUSTOM_ICON_SIZE / height);
        const drawWidth = width * scale;
        const drawHeight = height * scale;
        context.drawImage(
          image,
          (CUSTOM_ICON_SIZE - drawWidth) / 2,
          (CUSTOM_ICON_SIZE - drawHeight) / 2,
          drawWidth,
          drawHeight
        );
        resolve(canvas.toDataURL('image/png'));
      };
      image.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

/**
 * Editor for the voice call transcript between the agent and the customer.
 * `system` lines (call events such as hold or resume) can be edited, moved and removed, but not switched to another sender.
 * @example ` <CopilotVoiceTranscriptSection />`
 */
export function CopilotVoiceTranscriptSection({
  messages,
  onChange,
  disabled = false,
}: Props) {
  // Stable keys, kept alongside every mutation so React moves component
  // instances with their rows on reorder/removal instead of reusing instances
  // by position, which would leave DebouncedTextField state on the wrong row.
  const keyCounter = useRef(0);
  const stableKeys = useRef<string[]>([]);

  if (stableKeys.current.length < messages.length) {
    while (stableKeys.current.length < messages.length) {
      stableKeys.current.push(`voice-msg-${keyCounter.current++}`);
    }
  } else if (stableKeys.current.length > messages.length) {
    stableKeys.current.length = messages.length;
  }

  // Latest messages, so a debounced edit queued in an earlier render is applied
  // to the current transcript instead of restoring a stale copy of it.
  const latestMessages = useRef(messages);
  latestMessages.current = messages;

  /** Record the new messages as the latest and notify the parent. @example commit([]) */
  const commit = (next: TranscriptMessage[]) => {
    latestMessages.current = next;
    onChange(next);
  };

  /**
   * Update the text of the row with the given stable key, resolved against the latest messages.
   * @example updateMessage('voice-msg-0', 'Hello')
   */
  const updateMessage = (key: string, value: string) => {
    const index = stableKeys.current.indexOf(key);
    if (index === -1) return;
    commit(
      latestMessages.current.map((m, i) =>
        i === index ? { ...m, message: value } : m
      )
    );
  };

  // Per-row version of the latest icon change. An image conversion only applies its result if no other
  // icon change (a picked icon or a newer upload) happened for that row while it was running.
  const iconChangeVersions = useRef<Record<string, number>>({});

  /**
   * Marks a new icon change for the row and returns its version, invalidating any pending upload
   * @example const version = nextIconChangeVersion('voice-msg-4')
   */
  const nextIconChangeVersion = (key: string) => {
    const version = (iconChangeVersions.current[key] ?? 0) + 1;
    iconChangeVersions.current[key] = version;
    return version;
  };

  /**
   * Update the event icon of the system line with the given stable key; an empty value removes it.
   * @example updateEventType('voice-msg-4', 'hold')
   */
  const updateEventType = (key: string, value: EventIconValue) => {
    nextIconChangeVersion(key);
    const index = stableKeys.current.indexOf(key);
    if (index === -1) return;
    commit(
      latestMessages.current.map((m, i) =>
        i === index ? { ...m, eventType: value || undefined } : m
      )
    );
  };

  // Hidden file input shared by all rows, and the row whose custom image is being uploaded
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadRowKey = useRef<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  /**
   * Opens the file picker to upload a custom event icon for the row with the given stable key.
   * @example openIconUpload('voice-msg-4')
   */
  const openIconUpload = (key: string) => {
    uploadRowKey.current = key;
    setUploadError(null);
    fileInputRef.current?.click();
  };

  /**
   * Stores the chosen image, shrunk to a small PNG, as the custom icon of the pending row.
   * @param event - change event of the hidden file input
   * @example handleIconFile(event)
   */
  const handleIconFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const key = uploadRowKey.current;
    event.target.value = '';
    uploadRowKey.current = null;
    if (!file || !key) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file (PNG, JPG, SVG, GIF or WebP).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('The image is larger than 5 MB. Please choose a smaller image.');
      return;
    }
    const version = nextIconChangeVersion(key);
    try {
      const iconUrl = await toIconDataUrl(file);
      // Another icon was picked, or a newer image uploaded, for this row while converting: drop this result
      if (iconChangeVersions.current[key] !== version) return;
      const index = stableKeys.current.indexOf(key);
      if (index === -1) return;
      commit(
        latestMessages.current.map((m, i) =>
          i === index ? { ...m, eventType: 'custom', iconUrl } : m
        )
      );
    } catch (error) {
      if (iconChangeVersions.current[key] !== version) return;
      setUploadError(
        error instanceof Error ? error.message : 'The image could not be uploaded.'
      );
    }
  };

  /**
   * Add a new empty message from the given sender at the end. A system line is a call event such as
   * hold or resume.
   * @example addMessage('system')
   */
  const addMessage = (messageFrom: TranscriptMessage['messageFrom']) => {
    stableKeys.current.push(`voice-msg-${keyCounter.current++}`);
    commit([...latestMessages.current, { messageFrom, message: '' }]);
  };

  /** Remove the message at the given index. @example removeMessage(0) */
  const removeMessage = (index: number) => {
    stableKeys.current.splice(index, 1);
    commit(latestMessages.current.filter((_, i) => i !== index));
  };

  /** Toggle a message's sender between agent and customer. @example toggleSender(0) */
  const toggleSender = (index: number) =>
    commit(
      latestMessages.current.map((m, i) => {
        if (i !== index || m.messageFrom === 'system') return m;
        return {
          ...m,
          messageFrom: m.messageFrom === 'agent' ? 'customer' : 'agent',
        };
      })
    );

  /**
   * Move the message at `index` one slot up or down, swapping with its neighbour.
   * @example moveMessage(1, -1)
   */
  const moveMessage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= latestMessages.current.length) return;
    [stableKeys.current[index], stableKeys.current[target]] = [
      stableKeys.current[target],
      stableKeys.current[index],
    ];
    const next = [...latestMessages.current];
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {messages.map((msg, index) => (
        <Box
          key={stableKeys.current[index]}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            borderLeft: `2px solid ${
              senderBorderColor[msg.messageFrom] ?? '#ccc'
            }`,
            p: 1.5,
            backgroundColor: senderColor[msg.messageFrom] ?? '#fafafa',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip
              title={
                msg.messageFrom === 'system'
                  ? 'Call event'
                  : 'Click to switch sender'
              }
            >
              <Chip
                onClick={() => toggleSender(index)}
                icon={<PersonIcon style={{ fontSize: 14, color: '#333333' }} />}
                label={msg.messageFrom}
                size="small"
                sx={{
                  cursor: msg.messageFrom === 'system' ? 'default' : 'pointer',
                  backgroundColor: senderBorderColor[msg.messageFrom] ?? '#ccc',
                  color: '#333333',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'capitalize',
                  '& .MuiChip-icon': { color: '#333333', margin: '0px' },
                  '& .MuiChip-label': { padding: '0px' },
                  padding: '10px',
                }}
              />
            </Tooltip>
            {msg.messageFrom === 'system' && (
              <TextField
                select
                size="small"
                label="Event icon"
                value={
                  msg.eventType === 'custom' && !msg.iconUrl
                    ? ''
                    : msg.eventType ?? ''
                }
                onChange={(e) => {
                  const key = stableKeys.current[index];
                  if (e.target.value === UPLOAD_OPTION) {
                    openIconUpload(key);
                    return;
                  }
                  updateEventType(key, e.target.value as EventIconValue);
                }}
                sx={{ minWidth: 190, backgroundColor: '#fff' }}
              >
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'default'} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
                {msg.iconUrl && (
                  <MenuItem value="custom">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={msg.iconUrl}
                        alt=""
                        style={{ width: 16, height: 16, objectFit: 'contain' }}
                      />
                      Custom image
                    </Box>
                  </MenuItem>
                )}
                <MenuItem value={UPLOAD_OPTION}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddPhotoAlternateOutlined sx={eventIconStyle} />
                    {msg.iconUrl ? 'Upload new image…' : 'Upload custom image…'}
                  </Box>
                </MenuItem>
              </TextField>
            )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => moveMessage(index, -1)}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Move down"
                    disabled={index === messages.length - 1}
                    onClick={() => moveMessage(index, 1)}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove message">
                <IconButton
                  size="small"
                  onClick={() => removeMessage(index)}
                  sx={{ color: '#d32f2f' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <DebouncedTextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            label="Message"
            value={msg.message}
            onChange={(v) => updateMessage(stableKeys.current[index], v)}
          />
        </Box>
      ))}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        hidden
        onChange={handleIconFile}
      />
      {uploadError && (
        <CcfTypography variant="body2" sx={{ color: '#d32f2f' }}>
          {uploadError}
        </CcfTypography>
      )}

      {messages.length === 0 && (
        <CcfTypography
          variant="body2"
          sx={{ color: '#9e9e9e', textAlign: 'center', py: 2 }}
        >
          No messages.
        </CcfTypography>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addMessage('agent')}
          sx={{ textTransform: 'none' }}
        >
          Add Agent Message
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addMessage('customer')}
          sx={{ textTransform: 'none' }}
        >
          Add Customer Message
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addMessage('system')}
          sx={{ textTransform: 'none' }}
        >
          Add System Message
        </Button>
      </Box>
    </Box>
  );
}

export default CopilotVoiceTranscriptSection;
